import React from 'react';
import { Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import pdf from '../tokens/pdfStyles';
import { colors, fontFamily, severity, space, typeScale, coverDesign, coverFonts } from '../tokens/designTokens';
import type { Severity } from '../tokens/designTokens';
import { splitOrgName } from '../tokens/coverHelpers';

export const PdfPage: React.FC<{
    children?: React.ReactNode;
    orgName?: string;
    sectionLabel?: string;
    showHeader?: boolean;
}> = ({ children, orgName, sectionLabel, showHeader = true }) => (
    <Page size="A4" style={pdf.page} wrap>
        {showHeader && (
            <>
                <View fixed style={pdf.runningHeader}>
                    <Text style={pdf.runningHeaderHalf}>{orgName ?? ''}</Text>
                    <Text style={[pdf.runningHeaderHalf, { textAlign: 'right' }]}>{sectionLabel ?? 'AICA Credit Report'}</Text>
                </View>
                <View fixed style={pdf.runningHeaderRule} />
            </>
        )}
        {children}
        <View fixed style={pdf.runningFooterRule} />
        <View fixed style={pdf.runningFooter}>
            <Text>AICA Report — Confidential</Text>
            <Text
                render={({ pageNumber, totalPages }) => `${String(pageNumber).padStart(2, '0')} / ${String(totalPages).padStart(2, '0')}`}
            />
        </View>
    </Page>
);

export const PdfEyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <View style={pdf.eyebrow} wrap={false}>
        <View style={[pdf.eyebrowDot, { backgroundColor: colors.accent }]} />
        <Text style={pdf.eyebrowText}>{children}</Text>
    </View>
);

export const PdfSection: React.FC<{
    kicker?: string;
    title: string;
    subtitle?: string;
    index?: string;
    children: React.ReactNode;
    breakBefore?: boolean;
    minPresenceAhead?: number;
}> = ({ kicker, title, subtitle, index, children, breakBefore, minPresenceAhead }) => {
    const titleFont = fontFamily.display;
    // Default lookahead: keep the section header glued to at least 120pt of body.
    // Without this, the eyebrow + title can land at the bottom of a page and the
    // body starts on the next page, OR the body bleeds into the running footer.
    const presence = minPresenceAhead ?? 120;
    return (
        <View style={pdf.section} break={breakBefore} minPresenceAhead={presence}>
            <View style={pdf.sectionHeader} wrap={false}>
                {kicker ? (
                    <PdfEyebrow>
                        {index ? `${index} · ` : ''}
                        {kicker}
                    </PdfEyebrow>
                ) : null}
                <Text style={[pdf.sectionTitle, { fontFamily: titleFont }]}>{title}</Text>
                {subtitle ? <Text style={pdf.sectionSubtitle}>{subtitle}</Text> : null}
            </View>
            {children}
        </View>
    );
};

export const PdfParagraph: React.FC<{ children: React.ReactNode; style?: object }> = ({ children, style }) => (
    <Text style={[pdf.paragraph, style as any]}>{children}</Text>
);

export const PdfBulletList: React.FC<{ items: string[] }> = ({ items }) => (
    <View>
        {items.map((item, i) => (
            <View key={i} style={pdf.bullet} wrap={false}>
                <Text style={pdf.bulletDot}>—</Text>
                <Text style={pdf.bulletText}>{item}</Text>
            </View>
        ))}
    </View>
);

export const PdfKvList: React.FC<{ items: Array<{ label: string; value?: string | null }> }> = ({ items }) => (
    <View>
        {items
            .filter((x) => x.value != null && x.value !== '')
            .map((x, i) => (
                <View key={i} style={pdf.kvRow} wrap={false}>
                    <Text style={pdf.kvLabel}>{x.label}</Text>
                    <Text style={pdf.kvValue}>{x.value}</Text>
                </View>
            ))}
    </View>
);

export const PdfKpiCard: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
    <View style={pdf.kpiCard} wrap={false}>
        <Text style={pdf.kpiLabel}>{label}</Text>
        <Text style={pdf.kpiValue}>{value}</Text>
        {sub ? <Text style={pdf.kpiSub}>{sub}</Text> : null}
    </View>
);

export const PdfKpiGrid: React.FC<{ items: Array<{ label: string; value: string; sub?: string }> }> = ({ items }) => (
    <View style={pdf.kpiGrid}>
        {items.map((x, i) => (
            <PdfKpiCard key={i} {...x} />
        ))}
    </View>
);

export const PdfChip: React.FC<{ tone: Severity | 'info' | 'brand' | 'muted' | 'accent'; children: React.ReactNode }> = ({
    tone,
    children,
}) => {
    let bg: string = colors.neutralSoft;
    let fg: string = colors.ink40;
    if (tone === 'info') {
        bg = colors.infoSoft;
        fg = colors.info;
    } else if (tone === 'brand' || tone === 'accent') {
        bg = colors.accentSoft;
        fg = colors.accentDeep;
    } else if (tone === 'muted') {
        bg = colors.neutralSoft;
        fg = colors.ink40;
    } else {
        bg = severity[tone].bg;
        fg = severity[tone].fg;
    }
    return <Text style={[pdf.chip, { backgroundColor: bg, color: fg }]}>{children}</Text>;
};

type TableColumn = {
    key: string;
    header: string;
    width?: number | string;
    align?: 'left' | 'right' | 'center';
};

export const PdfDataTable: React.FC<{
    columns: TableColumn[];
    rows: Array<Record<string, React.ReactNode>>;
    zebra?: boolean;
    alignHeaderRight?: string[];
}> = ({ columns, rows, zebra = true }) => {
    const colStyle = (c: TableColumn) => {
        const base: any = { paddingHorizontal: 3 };
        if (typeof c.width === 'number') base.width = c.width;
        else if (typeof c.width === 'string') base.width = c.width;
        else base.flex = 1;
        base.textAlign = c.align ?? 'left';
        if (c.align === 'right') {
            base.fontFamily = fontFamily.mono;
        }
        return base;
    };
    // Per PM feedback on the page-break glitch: previously the table was
    // chunked into 18-row groups and each chunk wrapped in a `wrap={false}`
    // View. With wrapped multi-line cells, a chunk could exceed the page
    // height, and react-pdf's "force this onto next page" handling produced
    // a partial fragment at the top of the previous page (the glitch).
    //
    // New approach: render rows directly, no chunk wrapper. Each row keeps
    // `wrap={false}` so an individual row never splits mid-cell. The table
    // then breaks naturally between rows when a page fills. Header is marked
    // `fixed` so it repeats at the top of each page the table spans.
    return (
        <View style={pdf.table}>
            <View style={pdf.tableHeadRow} fixed>
                {columns.map((c) => (
                    <Text key={c.key} style={[pdf.tableHeadCell, colStyle(c)]}>
                        {c.header}
                    </Text>
                ))}
            </View>
            {rows.map((row, i) => (
                <View key={i} wrap={false} style={[pdf.tableRow, zebra && i % 2 === 1 ? pdf.tableRowAlt : undefined] as any}>
                    {columns.map((c) => (
                        <Text key={c.key} style={[pdf.tableCell, colStyle(c)]} wrap>
                            {row[c.key] as any}
                        </Text>
                    ))}
                </View>
            ))}
        </View>
    );
};

export const PdfDivider: React.FC = () => <View style={pdf.divider} />;

const monthStatusBg = StyleSheet.create({
    on_time: { backgroundColor: colors.pos },
    delayed: { backgroundColor: colors.warn },
    default: { backgroundColor: colors.neg },
    missing: { backgroundColor: colors.neutralSoft },
});

export const PdfMonthGrid: React.FC<{
    title: string;
    primary?: string;
    secondary?: string;
    cells: Array<{ label: string; short: string; yearShort: string; status: 'on_time' | 'delayed' | 'default' | 'missing' }>;
}> = ({ title, primary, secondary, cells }) => (
    <View style={pdf.monthGridWrap} wrap={false}>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={pdf.h4}>{title}</Text>
            {primary ? (
                <Text style={{ fontSize: typeScale.caption, color: colors.ink40 }}>
                    <Text style={{ color: colors.ink00, fontFamily: fontFamily.mono, fontWeight: 500 }}>{primary}</Text>
                    {secondary ? ` ${secondary}` : ''}
                </Text>
            ) : null}
        </View>
        <View style={pdf.monthGridRow}>
            {cells.map((c, i) => {
                const bg: any = monthStatusBg[c.status];
                const color = c.status === 'missing' ? colors.ink40 : colors.paper;
                return (
                    <View key={i} style={[pdf.monthCell, bg]} wrap={false}>
                        <Text style={{ fontSize: typeScale.micro, color, fontFamily: fontFamily.body, fontWeight: 600 }}>{c.short}</Text>
                        <Text style={{ fontSize: typeScale.micro - 1, color, opacity: 0.85, fontFamily: fontFamily.mono }}>
                            '{c.yearShort}
                        </Text>
                    </View>
                );
            })}
        </View>
        {/* Legend dots removed — the canonical legend now lives in PdfStatusBar
            below this grid (tinted-pill version per Figma 32:100094). Rendering
            both led to a duplicated row that read as visual noise. */}
    </View>
);

export const PdfSparkBars: React.FC<{ values: number[]; height?: number }> = ({ values, height = 32 }) => {
    const max = Math.max(1, ...values);
    return (
        <View style={[pdf.sparkbar, { height }]} wrap={false}>
            {values.map((v, i) => (
                <View key={i} style={[pdf.sparkbarCol, { height: Math.max(2, (v / max) * height) }]} />
            ))}
        </View>
    );
};

type StatusSegment = { label: string; count: number; color: string };
// Tinted-pill legend for compliance status. Matches Figma 32:100094 — each
// segment is a small tinted square with the status glyph inside, followed by
// an uppercase label. The percentage bar above is unchanged.
const STATUS_LEGEND_TONE: Record<string, { bg: string; fg: string; glyph: string }> = {
    'On time': { bg: '#D1FAE5', fg: '#059669', glyph: '✓' },
    Delayed: { bg: '#FEF3C7', fg: '#D97706', glyph: '!' },
    Default: { bg: '#FEE2E2', fg: '#DC2626', glyph: '✕' },
    Missing: { bg: '#F3F4F6', fg: '#9CA3AF', glyph: '—' },
};

export const PdfStatusBar: React.FC<{ segments: StatusSegment[] }> = ({ segments }) => {
    const total = segments.reduce((s, seg) => s + seg.count, 0);
    if (total <= 0) return null;
    return (
        <View wrap={false}>
            <View style={{ flexDirection: 'row', height: 8, borderRadius: 2, overflow: 'hidden' }}>
                {segments.map((seg, i) =>
                    seg.count > 0 ? (
                        <View
                            key={i}
                            style={{
                                flex: seg.count,
                                backgroundColor: seg.color,
                            }}
                        />
                    ) : null,
                )}
            </View>
            {/* Single horizontal row, never wraps — the four known status buckets fit
                comfortably across the page width. Dropped flexWrap so tight pages
                don't push the trailing pills onto a new line and break the optical
                rhythm. Counts moved off the legend (they're already implicit in the
                percentage bar above) so the row matches Figma 32:100094. */}
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 6, alignItems: 'center' }}>
                {segments.map((seg, i) => {
                    if (seg.count <= 0) return null;
                    const tone = STATUS_LEGEND_TONE[seg.label] ?? { bg: seg.color, fg: '#FFFFFF', glyph: '·' };
                    return (
                        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }} wrap={false}>
                            <View
                                style={{
                                    width: 9,
                                    height: 9,
                                    backgroundColor: tone.bg,
                                    borderRadius: 1.5,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 5.5,
                                        color: tone.fg,
                                        fontFamily: fontFamily.sansBold,
                                        lineHeight: 1,
                                        textAlign: 'center',
                                    }}
                                >
                                    {tone.glyph}
                                </Text>
                            </View>
                            <Text
                                style={{
                                    fontSize: typeScale.micro,
                                    color: colors.ink40,
                                    letterSpacing: 0.5,
                                    textTransform: 'uppercase',
                                    lineHeight: 1,
                                    fontFamily: fontFamily.sansBold,
                                }}
                            >
                                {seg.label}
                            </Text>
                        </View>
                    );
                })}
            </View>
        </View>
    );
};

export const PdfDelayBars: React.FC<{ cells: Array<{ short: string; yearShort: string; delayDays?: number; status: string }> }> = ({
    cells,
}) => {
    const max = Math.max(1, ...cells.map((c) => c.delayDays ?? 0));
    if (max <= 1) return null;
    return (
        <View wrap={false} style={{ marginTop: 8 }}>
            {/* Title now carries the "max Nd" annotation that used to live on the
                bottom row. The previous x-axis row had three cryptic labels
                (first month / max / last month) that read like a broken legend
                — and the month grid right above already labels every column,
                so a duplicated x-axis here was redundant. Dropped it.
                */}
            <View
                style={{
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                }}
            >
                <Text style={{ fontSize: typeScale.micro, color: colors.ink40, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                    Delay days (per month)
                </Text>
                <Text style={{ fontSize: typeScale.micro, color: colors.ink20, fontFamily: fontFamily.mono }}>Max {max}d</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 2, height: 28 }}>
                {cells.map((c, i) => {
                    const v = c.delayDays ?? 0;
                    const color = c.status === 'default' ? colors.neg : v > 0 ? colors.warn : colors.neutralSoft;
                    return (
                        <View
                            key={i}
                            style={{
                                flex: 1,
                                height: v > 0 ? Math.max(2, (v / max) * 28) : 2,
                                backgroundColor: color,
                                borderRadius: 1,
                            }}
                        />
                    );
                })}
            </View>
        </View>
    );
};

export const PdfCard: React.FC<{
    title: string;
    meta?: string;
    children?: React.ReactNode;
    tone?: 'default' | 'flat' | 'accent' | 'warn';
}> = ({ title, meta, children, tone = 'default' }) => {
    const toneStyle = tone === 'flat' ? pdf.cardFlat : tone === 'accent' ? pdf.cardAccent : tone === 'warn' ? pdf.cardWarn : undefined;
    return (
        <View style={[pdf.card, toneStyle] as any} wrap={false}>
            <Text style={pdf.cardTitle}>{title}</Text>
            {meta ? <Text style={pdf.cardMeta}>{meta}</Text> : null}
            <View>{typeof children === 'string' ? <Text style={pdf.cardBody}>{children}</Text> : children}</View>
        </View>
    );
};

export const PdfCardGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => <View style={pdf.cardGrid}>{children}</View>;

export const PdfCover: React.FC<{
    orgName: string;
    subtitle?: string;
    meta: Array<{ label: string; value: string }>;
    generatedOn: string;
}> = ({ orgName, meta, generatedOn }) => {
    const { main, suffix } = splitOrgName(orgName);
    const c = coverDesign;
    const f = coverFonts.pdf;

    // Layout strategy (matches what the screen preview does):
    // Wrap the entire cover in a single inner View with an EXPLICIT height equal
    // to the A4 page height. With a known main-axis size, react-pdf's flex layout
    // honours `justifyContent: 'space-between'` reliably — top group stays at the
    // top, bottom group is pinned to the bottom.
    //
    // Why not put padding/flex on <Page> directly? react-pdf's <Page> doesn't
    // propagate its A4 height to its children's flex container in a way that
    // makes `flexGrow: 1`, `marginTop: 'auto'`, or `position: 'absolute'` behave
    // consistently. An inner View with `height: c.pageHeight` removes that
    // ambiguity once and for all.
    return (
        <Page size="A4" wrap={false} style={{ backgroundColor: colors.cover, fontFamily: f.sans }}>
            <View
                style={{
                    height: c.pageHeight,
                    paddingTop: c.paddingTop,
                    paddingBottom: c.paddingBottom,
                    paddingLeft: c.paddingX,
                    paddingRight: c.paddingX,
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    color: colors.coverInk,
                }}
            >
                {/* TOP GROUP — wordmark, title, rule, metadata. Sits at page top. */}
                <View>
                    {/* Powered by AICA — top-left wordmark */}
                    <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: c.poweredByGap }}>
                        <Text style={{ fontSize: c.poweredBySize, color: colors.coverInkSoft, fontFamily: f.sans, fontWeight: 500 }}>
                            Powered by
                        </Text>
                        <Text
                            style={{
                                fontSize: c.aicaWordmarkSize,
                                color: colors.coverInk,
                                fontFamily: f.display,
                                fontWeight: 500,
                                letterSpacing: c.aicaWordmarkTrackingEm * c.aicaWordmarkSize,
                            }}
                        >
                            AICA
                        </Text>
                    </View>

                    {/* Title */}
                    <View style={{ marginTop: c.topToTitle }}>
                        <Text
                            style={{
                                fontSize: c.titleSize,
                                lineHeight: c.titleLineHeight,
                                letterSpacing: c.titleTrackingEm * c.titleSize,
                                color: colors.coverInk,
                                fontFamily: f.display,
                                fontWeight: 400,
                            }}
                        >
                            {main}
                        </Text>
                        {suffix ? (
                            <Text
                                style={{
                                    fontSize: c.suffixSize,
                                    lineHeight: c.suffixLineHeight,
                                    letterSpacing: c.suffixTrackingEm * c.suffixSize,
                                    color: colors.coverInkSoft,
                                    fontFamily: f.display,
                                    fontWeight: 400,
                                    marginTop: c.suffixMarginTop,
                                }}
                            >
                                {suffix}
                            </Text>
                        ) : null}
                    </View>

                    {/* Teal hairline */}
                    <View
                        style={{
                            width: c.ruleWidth,
                            height: c.ruleHeight,
                            backgroundColor: colors.coverRule,
                            marginTop: c.ruleMarginTop,
                            marginBottom: c.ruleMarginBottom,
                        }}
                    />

                    {/* Metadata rows */}
                    <View>
                        {meta.map((m, i) => (
                            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', minHeight: c.metaRowHeight }} wrap={false}>
                                <Text
                                    style={{
                                        width: c.metaLabelWidth,
                                        fontSize: c.metaLabelSize,
                                        fontFamily: f.mono,
                                        fontWeight: 400,
                                        color: colors.coverInkMuted,
                                        letterSpacing: c.metaLabelTrackingEm * c.metaLabelSize,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {m.label}
                                </Text>
                                <Text
                                    style={{ fontSize: c.metaValueSize, fontFamily: f.mono, fontWeight: 400, color: colors.coverInkSoft }}
                                >
                                    {m.value}
                                </Text>
                            </View>
                        ))}
                    </View>
                </View>

                {/* BOTTOM GROUP — "Company Due Diligence Report" + generated date.
                    Pinned to the bottom by the parent's `justifyContent: 'space-between'`. */}
                <View wrap={false}>
                    <Text
                        style={{
                            fontSize: c.bottomLabelSize,
                            fontFamily: f.sans,
                            fontWeight: 500,
                            color: colors.coverInk,
                            letterSpacing: c.bottomLabelTrackingEm * c.bottomLabelSize,
                            textTransform: 'uppercase',
                        }}
                    >
                        Company Due Diligence Report
                    </Text>
                    <Text
                        style={{
                            marginTop: c.bottomGap,
                            fontSize: c.bottomDateSize,
                            fontFamily: f.mono,
                            fontWeight: 400,
                            color: colors.coverInkSoft,
                            letterSpacing: c.bottomDateTrackingEm * c.bottomDateSize,
                        }}
                    >
                        Generated · {generatedOn}
                    </Text>
                </View>
            </View>
        </Page>
    );
};
